import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { Location } from '@angular/common';

import { ScrollSpyService } from '@finder/shared/services/scroll-spy/scroll-spy.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

@Directive({
    selector: '[finderClickScrollTo]',
    standalone: true
})
export class ClickScrollToDirective {
    @Input() scrollId: string;
    @Input() offsetElement: string;

    constructor(
        private window: WindowRef,
        private location: Location,
        private elementRef: ElementRef,
        private scrollSpyService: ScrollSpyService
    ) { }

    @HostListener('click', ['$event'])
    async onClick(event) {
        this.scrollSpyService.pauseScroll$.next(true);

        const window = this.getWindowRef();
        const document = window.document;

        // The scrollId can be something like `/path/to/url#scroll-to`
        // We need to chop it at the '#' to get the ID
        this.scrollId = this.scrollId.split('#').at(-1);

        const target = document.getElementById(this.scrollId);
        if (!target) {
            return;
        }

        event.preventDefault();

        // Check if we are have an offset element
        let scrollOffset = 0;
        const el = this.getElementRef();
        const offsetEl = el.closest(this.offsetElement);
        if (offsetEl) {
            const navRect = offsetEl.getBoundingClientRect();

            // Check if target has padding/margin top
            // The computed style will always return a value (default 0px)
            let padding = 0;
            const targetStyles = window.getComputedStyle(target);
            const targetPadTop = Number(targetStyles.getPropertyValue('padding-top').match(/(\d+)/)[0]);
            if (targetPadTop === 0) {
                padding = 10;
            }

            // Set offset
            scrollOffset = navRect.height + padding;
        }

        if (scrollOffset === 0) {
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        } else {
            const rect = target.getBoundingClientRect();
            const elTop = Math.round(window.scrollY - document.documentElement.clientTop + rect.top - scrollOffset);
            await this.scrollTo(elTop);
            this.scrollSpyService.pauseScroll$.next(false);
        }

        // Set hash
        this.location.replaceState(`${this.location.path()}/#${this.scrollId}`);
    }

    /**
     * @name            scrollTo
     * @description     Smoothly scrolls the window vertically to the specified Y-coordinate and
     *                  returns a Promise that resolves once the scrolling is complete.
     * @param           targetY The vertical position (in pixels) to scroll to.
     * @returns         Promise that resolves when scrolling is done
     */
    private scrollTo(targetY: number): Promise<void> {
        const window = this.getWindowRef();

        return new Promise((resolve) => {
            const checkScroll = () => {

                // scroll finishes when scrollY is pretty close to the targetY
                if (Math.abs(window.scrollY - targetY) <= 2) {
                    resolve();
                } else {
                    window.requestAnimationFrame(checkScroll);
                }
            };

            window.scrollTo({ top: targetY, behavior: 'smooth' });
            window.requestAnimationFrame(checkScroll);
        });
    }

    /* istanbul ignore next */
    getWindowRef(): Window {
        return this.window.nativeWindow;
    }

    /* istanbul ignore next */
    getElementRef() {
        return this.elementRef.nativeElement;
    }
}
